import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Video, 
  Calendar, 
  Clock, 
  Users, 
  User, 
  CheckCircle, 
  XCircle, 
  PlayCircle,
  AlertCircle,
  ArrowLeft,
  Share2,
  Copy,
  LogIn,
  UserX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { getUserEffectiveTimezone } from '../utils/timezoneUtils';
import ResponsiveHeader from '../components/ResponsiveHeader';
import Footer from '../components/Footer';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const GroupClassPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showCapacityFullModal, setShowCapacityFullModal] = useState(false);
  const [isParticipant, setIsParticipant] = useState(false);

  const fetchClassData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE}/api/group-classes/${slug}`, { headers });

      if (response.ok) {
        const data = await response.json();
        setClassData(data);
        
        // Check if current user is a participant
        if (isAuthenticated && user && data.jitsi_room_url) {
          // If we get jitsi_room_url, user is either trainer or participant
          setIsParticipant(true);
        }
      } else if (response.status === 404) {
        setError('Group class not found');
      } else {
        setError('Failed to load class details');
      }
    } catch (err) {
      console.error('Error fetching class:', err);
      setError('Failed to load class details');
    } finally {
      setLoading(false);
    }
  }, [slug, isAuthenticated, user]);

  useEffect(() => {
    fetchClassData();
  }, [fetchClassData]);

  const handleJoinClass = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    // Check if class is full before making request
    if (classData.is_full) {
      setShowCapacityFullModal(true);
      return;
    }

    setJoining(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/group-classes/${classData.id}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast({ 
          title: 'Successfully Joined!', 
          description: 'You will receive a confirmation email with the class link.' 
        });
        setIsParticipant(true);
        fetchClassData(); // Refresh to get updated data
      } else {
        const error = await response.json();
        
        // Check if it's a capacity full error (race condition case)
        if (error.detail?.error_code === 'CLASS_FULL' || error.detail === 'Class is full') {
          setShowCapacityFullModal(true);
          fetchClassData(); // Refresh to update UI
        } else {
          toast({ 
            title: 'Cannot Join', 
            description: typeof error.detail === 'object' ? error.detail.message : (error.detail || 'Failed to join class'), 
            variant: 'destructive' 
          });
        }
      }
    } catch (err) {
      toast({ 
        title: 'Error', 
        description: 'Failed to join class', 
        variant: 'destructive' 
      });
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveClass = async () => {
    if (!window.confirm('Are you sure you want to leave this class?')) return;

    setLeaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/group-classes/${classData.id}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: 'Left Class', description: 'You have left the class.' });
        setIsParticipant(false);
        fetchClassData();
      } else {
        const error = await response.json();
        toast({ 
          title: 'Error', 
          description: error.detail || 'Failed to leave class', 
          variant: 'destructive' 
        });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to leave class', variant: 'destructive' });
    } finally {
      setLeaving(false);
    }
  };

  const joinMeeting = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/group-classes/${classData.id}/jitsi-link`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.jitsi_room_url, '_blank');
      } else {
        const error = await response.json();
        toast({ 
          title: 'Access Denied', 
          description: error.detail || 'You must be registered to join this class', 
          variant: 'destructive' 
        });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to get meeting link', variant: 'destructive' });
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({ title: 'Link Copied', description: 'Share link copied to clipboard!' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      upcoming: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: Calendar, label: 'Upcoming' },
      live: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: PlayCircle, label: 'Live Now' },
      completed: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-400', icon: CheckCircle, label: 'Completed' },
      cancelled: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: XCircle, label: 'Cancelled' }
    };
    
    const style = styles[status] || styles.upcoming;
    const Icon = style.icon;
    
    return (
      <Badge className={`${style.bg} ${style.text} gap-1 text-sm px-3 py-1`}>
        <Icon className="w-4 h-4" />
        {style.label}
      </Badge>
    );
  };

  const formatDateTime = (date, time, trainerTimezone, utcTime) => {
    try {
      // Get user's effective timezone (profile setting or browser fallback)
      const userTimezone = getUserEffectiveTimezone(user);
      
      // If we have UTC time, use it directly for accurate conversion
      if (utcTime) {
        const utcDate = new Date(utcTime);
        
        // Format in user's timezone
        const localFormatted = formatInTimeZone(
          utcDate, 
          userTimezone, 
          'EEEE, MMMM d, yyyy \'at\' h:mm a'
        );
        
        // Get timezone abbreviation
        const userTzAbbr = formatInTimeZone(utcDate, userTimezone, 'zzz');
        
        return `${localFormatted} (${userTzAbbr})`;
      }
      
      // Fallback to scheduled date/time if no UTC time
      const tz = trainerTimezone || 'America/Toronto';
      const dateTimeStr = `${date}T${time}:00`;
      
      if (userTimezone === tz) {
        return format(parseISO(dateTimeStr), 'EEEE, MMMM d, yyyy \'at\' h:mm a');
      }
      
      const trainerDate = new Date(`${date}T${time}:00`);
      const localFormatted = formatInTimeZone(
        trainerDate, 
        userTimezone, 
        'EEEE, MMMM d, yyyy \'at\' h:mm a'
      );
      const userTzAbbr = formatInTimeZone(trainerDate, userTimezone, 'zzz');
      
      return `${localFormatted} (${userTzAbbr})`;
    } catch (e) {
      console.error('Error formatting date:', e);
      return `${date} at ${time}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <ResponsiveHeader />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <ResponsiveHeader />
        <div className="container mx-auto px-4 py-12">
          <Card className="max-w-lg mx-auto">
            <CardContent className="py-12 text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {error || 'Class Not Found'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This group class may have been removed or does not exist.
              </p>
              <Button onClick={() => navigate('/')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const isTrainer = isAuthenticated && user?.user_id === classData.trainer_id;
  // canJoin: user CAN potentially join (not already joined, not trainer)
  // showJoinButton: whether to show the join button (regardless of capacity)
  const showJoinButton = !isParticipant && !isTrainer && classData.status !== 'completed' && classData.status !== 'cancelled';
  const canJoin = classData.can_join && !isParticipant && !isTrainer;
  const canLeave = isParticipant && classData.status === 'upcoming';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <ResponsiveHeader />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Status Banner for Cancelled Classes */}
        {classData.status === 'cancelled' && (
          <Alert variant="destructive" className="mb-6">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              This class has been cancelled by the trainer.
            </AlertDescription>
          </Alert>
        )}

        {/* Live Banner */}
        {classData.status === 'live' && (
          <Alert className="mb-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <PlayCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-300">
              <strong>This class is live now!</strong> 
              {isParticipant ? ' Click below to join the meeting.' : ' Register to join.'}
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content Card */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 sm:p-8 text-white">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Video className="w-8 h-8" />
                    {getStatusBadge(classData.status)}
                    {classData.class_type === 'private' && (
                      <Badge variant="secondary" className="bg-white/20 text-white">
                        Private Class
                      </Badge>
                    )}
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold mb-2">
                    {classData.title}
                  </h1>
                  <p className="text-red-100 text-lg">
                    {classData.short_description}
                  </p>
                </div>
                
                {/* Share Button */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={copyShareLink}
                  className="bg-white/20 hover:bg-white/30 text-white shrink-0"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 sm:p-8 space-y-6">
              {/* Class Details Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <Calendar className="w-6 h-6 text-red-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Date & Time</p>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">
                    {formatDateTime(classData.scheduled_date, classData.start_time, classData.trainer_timezone, classData.start_time_utc)}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <Clock className="w-6 h-6 text-red-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Duration</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {classData.duration_minutes} minutes
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <Users className="w-6 h-6 text-red-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Participants</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {classData.current_participants} / {classData.max_participants}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                  <User className="w-6 h-6 text-red-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Trainer</p>
                  <Link 
                    to={`/trainer/${classData.trainer_slug}`}
                    className="font-medium text-red-600 hover:underline"
                  >
                    {classData.trainer_name}
                  </Link>
                </div>
              </div>

              {/* Capacity Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 dark:text-gray-400">Available Spots</span>
                  <span className={`font-medium ${classData.is_full ? 'text-red-600' : 'text-green-600'}`}>
                    {classData.remaining_spots} remaining
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      classData.is_full ? 'bg-red-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${(classData.current_participants / classData.max_participants) * 100}%` }}
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  About This Class
                </h3>
                <div className="prose dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 whitespace-pre-line">
                  {classData.detailed_description}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                {classData.status === 'cancelled' ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      This class has been cancelled and is no longer available.
                    </AlertDescription>
                  </Alert>
                ) : classData.status === 'completed' ? (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      This class has already ended.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Join Class Button - with tooltip when full */}
                    {showJoinButton && (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex-1">
                              <Button
                                onClick={handleJoinClass}
                                disabled={joining || classData.is_full}
                                className={`w-full ${classData.is_full ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                                size="lg"
                              >
                                {joining ? (
                                  <>
                                    <span className="animate-spin mr-2">⏳</span>
                                    Joining...
                                  </>
                                ) : classData.is_full ? (
                                  <>
                                    <UserX className="w-5 h-5 mr-2" />
                                    Class Full
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Join Class
                                  </>
                                )}
                              </Button>
                            </div>
                          </TooltipTrigger>
                          {classData.is_full && (
                            <TooltipContent side="top" className="max-w-xs text-center bg-gray-900 text-white p-3">
                              <p className="font-semibold text-amber-400">Class Capacity Full</p>
                              <p className="text-sm mt-1">This class has reached its maximum capacity. You cannot join at this time.</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    {/* Class Full Message for non-authenticated users */}
                    {!isAuthenticated && classData.is_full && (
                      <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                        <UserX className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-800 dark:text-amber-300">
                          This class has reached its maximum capacity ({classData.max_participants} participants).
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Already Joined - Show Join Meeting or Leave */}
                    {isParticipant && !isTrainer && (
                      <>
                        {(classData.status === 'live' || classData.status === 'upcoming') && (
                          <Button
                            onClick={joinMeeting}
                            className={`flex-1 ${classData.status === 'live' ? 'bg-green-600 hover:bg-green-700 animate-pulse' : 'bg-blue-600 hover:bg-blue-700'}`}
                            size="lg"
                          >
                            <Video className="w-5 h-5 mr-2" />
                            {classData.status === 'live' ? 'Join Live Class' : 'Preview Meeting Room'}
                          </Button>
                        )}
                        {canLeave && (
                          <Button
                            onClick={handleLeaveClass}
                            disabled={leaving}
                            variant="outline"
                            size="lg"
                          >
                            {leaving ? 'Leaving...' : 'Leave Class'}
                          </Button>
                        )}
                      </>
                    )}

                    {/* Trainer View */}
                    {isTrainer && (
                      <Button
                        onClick={joinMeeting}
                        className="bg-green-600 hover:bg-green-700 flex-1"
                        size="lg"
                      >
                        <Video className="w-5 h-5 mr-2" />
                        Join as Host
                      </Button>
                    )}

                    {/* Not Logged In - Show Login Prompt */}
                    {!isAuthenticated && classData.can_join && (
                      <Button
                        onClick={() => setShowLoginPrompt(true)}
                        className="bg-red-600 hover:bg-red-700 flex-1"
                        size="lg"
                      >
                        <LogIn className="w-5 h-5 mr-2" />
                        Sign In to Join
                      </Button>
                    )}
                  </div>
                )}

                {/* Registration Status */}
                {isParticipant && !isTrainer && (
                  <Alert className="mt-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800 dark:text-green-300">
                      {classData.status === 'live' 
                        ? 'You are registered for this class. The class is now live - click "Join Live Class" to participate!'
                        : classData.status === 'completed'
                        ? 'You were registered for this class. The class has ended.'
                        : 'You are registered for this class. You will receive reminder emails before it starts.'}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />

      {/* Login Prompt Modal */}
      <Dialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="w-5 h-5" />
              Sign In Required
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You must be a member to join this group class. Please sign in or create an account to continue.
            </p>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                After signing in, you will be able to join this class and receive the Jitsi meeting link.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowLoginPrompt(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => navigate(`/login?redirect=/group-class/${slug}`)}
              className="bg-red-600 hover:bg-red-700"
            >
              Sign In
            </Button>
            <Button 
              onClick={() => navigate(`/register?redirect=/group-class/${slug}`)}
              variant="outline"
            >
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Capacity Full Modal */}
      <Dialog open={showCapacityFullModal} onOpenChange={setShowCapacityFullModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <UserX className="w-5 h-5" />
              Class Capacity Full
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-300">
                This class has reached its maximum participant capacity.
              </AlertDescription>
            </Alert>
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Kapasite:</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {classData?.current_participants} / {classData?.max_participants}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                <div 
                  className="bg-red-600 h-2 rounded-full"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-4 text-sm">
              You cannot join this class at this time. Please check other available classes.
            </p>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCapacityFullModal(false)}
            >
              Close
            </Button>
            <Button 
              onClick={() => {
                setShowCapacityFullModal(false);
                navigate('/');
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Browse Other Classes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupClassPage;
